import type { MutationOptions, UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';

import {
  getBannerMessagesApi,
  getNotificationsHistoryApi,
  updateNotificationHistoryStatusApi,
  updateNotificationsHistoryStatusApi,
  updateNotificationsRoofTopHistoryStatus,
} from 'api/notifications';
import type {
  GetBannerMessagesResponseDTO,
  GetBannerMessagesResponseParamsDTO,
  GetNotificationsHistoryParamsDTO,
  GetNotificationsHistoryResponseDTO,
} from 'declarations/dto/notifications';
import { RequestQueryKeysE } from 'declarations/enums/react-query';
import type { ApiErrorI } from 'declarations/interfaces/errors';

type UseUpdateNotificationHistoryStatusParamsT = {
  onSuccess: MutationOptions<void, ApiErrorI, string>['onSuccess'];
};

type UseUpdateNotificationsHistoryStatusParamsT = {
  onSuccess: MutationOptions<void, ApiErrorI>['onSuccess'];
};

export const useFetchNotificationsHistory = (
  params: GetNotificationsHistoryParamsDTO
): UseQueryResult<GetNotificationsHistoryResponseDTO, ApiErrorI> =>
  useQuery<GetNotificationsHistoryResponseDTO, ApiErrorI>(
    [RequestQueryKeysE.FetchNotificationsHistory, params],
    () => getNotificationsHistoryApi(params)
  );

export const useUpdateNotificationHistoryStatus = ({
  onSuccess,
}: UseUpdateNotificationHistoryStatusParamsT): UseMutationResult<void, ApiErrorI, string> =>
  useMutation<void, ApiErrorI, string>(
    [RequestQueryKeysE.UpdateNotificationHistoryStatus],
    (notificationId: string) => updateNotificationHistoryStatusApi(notificationId),
    { onSuccess }
  );

export const useUpdateNotificationsHistoryStatus = ({
  onSuccess,
}: UseUpdateNotificationsHistoryStatusParamsT): UseMutationResult<void, ApiErrorI, void> =>
  useMutation<void, ApiErrorI>(
    [RequestQueryKeysE.UpdateNotificationsHistoryStatus],
    updateNotificationsHistoryStatusApi,
    { onSuccess }
  );

export const useFetchBannerMessages = (
  params: GetBannerMessagesResponseParamsDTO
): UseQueryResult<GetBannerMessagesResponseDTO, ApiErrorI> =>
  useQuery<GetBannerMessagesResponseDTO, ApiErrorI>(
    [RequestQueryKeysE.FetchBannerMessages, params],
    () => getBannerMessagesApi(params)
  );

export const useUpdateNotificationsRoofTopHistoryStatus = ({
  onSuccess,
}: UseUpdateNotificationHistoryStatusParamsT): UseMutationResult<void, ApiErrorI, string> =>
  useMutation<void, ApiErrorI, string>(
    ['updateNotificationsRoofTopHistoryStatus'],
    (roofTopId: string) => updateNotificationsRoofTopHistoryStatus(roofTopId),
    { onSuccess }
  );
